export default {
  // Common
  common: {
    loading: 'Loading...',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    search: 'Search',
    filter: 'Filter',
    reset: 'Reset',
    submit: 'Submit',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    yes: 'Yes',
    no: 'No',
    success: 'Success',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
    all: 'All',
    none: 'None',
    enabled: 'Enabled',
    disabled: 'Disabled',
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    completed: 'Completed',
    failed: 'Failed',
    view: 'View',
    details: 'Details',
    actions: 'Actions',
    status: 'Status',
    date: 'Date',
    time: 'Time',
    amount: 'Amount',
    price: 'Price',
    total: 'Total',
    fee: 'Fee',
    balance: 'Balance',
    available: 'Available',
    locked: 'Locked',
    copy: 'Copy',
    copied: 'Copied!',
    download: 'Download',
    upload: 'Upload',
    refresh: 'Refresh',
    more: 'More',
    less: 'Less',
    optional: 'Optional',
    required: 'Required',
    clear: 'Clear',
    retry: 'Retry',
    name: 'Name',
    manage: 'Manage',
    submitted: 'Submitted',
  },

  // Navigation
  nav: {
    home: 'Home',
    dashboard: 'Dashboard',
    overview: 'Overview',
    portfolio: 'Portfolio',
    trade: 'Trade',
    tradeNow: 'Trade Now',
    markets: 'Markets',
    wallet: 'Wallet',
    orders: 'Orders',
    myAssets: 'My Assets',
    createAsset: 'Create Asset',
    kyc: 'KYC',
    settings: 'Settings',
    profile: 'Profile',
    security: 'Security',
    notifications: 'Notifications',
    help: 'Help',
    logout: 'Logout',
    login: 'Login',
    register: 'Register',
  },

  // Home page
  home: {
    hero: {
      title: 'Tokenize Your Assets',
      subtitle: 'Transform real-world assets into tradeable digital tokens. Unlock liquidity, enable fractional ownership, and access global markets.',
      getStarted: 'Get Started',
      exploreAssets: 'Explore Assets',
    },
    features: {
      title: 'Everything You Need',
      subtitle: 'A complete platform for digital asset tokenization and trading',
      tokenization: {
        title: 'Asset Tokenization',
        description: 'Transform real-world assets into digital tokens with full regulatory compliance.',
      },
      trading: {
        title: 'Advanced Trading',
        description: 'Trade tokens with our high-performance matching engine supporting limit and market orders.',
      },
      security: {
        title: 'Secure & Compliant',
        description: 'Enterprise-grade security with KYC/AML compliance built into every transaction.',
      },
      revenue: {
        title: 'Revenue Distribution',
        description: 'Automated dividend and revenue distribution to token holders.',
      },
    },
    cta: {
      title: 'Ready to Start Tokenizing?',
      subtitle: 'Join thousands of asset owners who have already tokenized their assets on TokenForge.',
      button: 'Create Your Account',
    },
  },

  // Auth
  auth: {
    login: 'Login',
    loginRequired: 'Please login to continue',
    loginPage: {
      title: 'Login',
      subtitle: 'Welcome back! Please sign in to continue.',
      email: 'Email',
      password: 'Password',
      rememberMe: 'Remember me',
      forgotPassword: 'Forgot password?',
      noAccount: "Don't have an account?",
      signUp: 'Sign up',
      submit: 'Sign In',
      loading: 'Signing in...',
    },
    register: {
      title: 'Create Account',
      subtitle: 'Join TokenForge to start trading.',
      username: 'Username',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      displayName: 'Display Name',
      termsAgree: 'I agree to the Terms of Service and Privacy Policy',
      haveAccount: 'Already have an account?',
      signIn: 'Sign in',
      submit: 'Create Account',
      loading: 'Creating account...',
    },
    forgotPassword: {
      title: 'Reset Password',
      subtitle: 'Enter your email to receive a password reset link.',
      email: 'Email',
      submit: 'Send Reset Link',
      loading: 'Sending...',
      success: 'Password reset email sent. Please check your inbox.',
      backToLogin: 'Back to login',
    },
    twoFactor: {
      title: 'Two-Factor Authentication',
      subtitle: 'Enter the verification code from your authenticator app.',
      code: 'Verification Code',
      submit: 'Verify',
      loading: 'Verifying...',
      useBackupCode: 'Use backup code',
    },
    errors: {
      invalidCredentials: 'Invalid email or password',
      emailRequired: 'Email is required',
      passwordRequired: 'Password is required',
      usernameRequired: 'Username is required',
      invalidEmail: 'Invalid email format',
      passwordMismatch: 'Passwords do not match',
      weakPassword: 'Password is too weak',
      usernameTaken: 'Username is already taken',
      emailTaken: 'Email is already registered',
      accountLocked: 'Account is locked. Please try again later.',
      tooManyAttempts: 'Too many attempts. Please try again later.',
    },
  },

  // Dashboard
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome back, {name}!',
    totalBalance: 'Total Balance',
    todayPnL: "Today's P&L",
    portfolioValue: 'Portfolio Value',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    deposit: 'Deposit',
    withdraw: 'Withdraw',
    trade: 'Trade',
    viewAll: 'View All',
  },

  // Settings
  settings: {
    title: 'Settings',
    preferences: {
      title: 'Preferences',
      language: 'Language',
      timezone: 'Timezone',
      theme: 'Theme',
      themeLight: 'Light',
      themeDark: 'Dark',
      themeSystem: 'System',
    },
    trading: {
      title: 'Trading Settings',
      defaultSlippage: 'Default Slippage Tolerance',
      slippageDescription: 'Maximum price difference you are willing to accept',
    },
    security: {
      title: 'Security Settings',
      withdrawWhitelist: 'Withdrawal Whitelist',
      withdrawWhitelistDescription: 'Only allow withdrawals to whitelisted addresses',
    },
    notifications: {
      title: 'Notifications',
      email: 'Email Notifications',
      push: 'Push Notifications',
      trade: 'Trade Notifications',
      security: 'Security Alerts',
      marketing: 'Marketing & Promotions',
    },
    saveAll: 'Save All Settings',
    saving: 'Saving...',
    saved: 'Settings saved successfully',
  },

  // Security
  security: {
    title: 'Security',
    twoFactor: {
      title: 'Two-Factor Authentication',
      description: 'Add an extra layer of security to your account',
      enabled: '2FA is enabled',
      disabled: '2FA is not enabled',
      enable: 'Enable 2FA',
      disable: 'Disable 2FA',
      setup: {
        title: 'Set up 2FA',
        step1: 'Download an authenticator app',
        step1Description: 'Download Google Authenticator, Authy, or any other TOTP app.',
        step2: 'Scan the QR code',
        step2Description: 'Open your authenticator app and scan the QR code below.',
        step3: 'Enter verification code',
        step3Description: 'Enter the 6-digit code from your authenticator app.',
        secretKey: 'Secret Key (manual entry)',
        verificationCode: 'Verification Code',
        submit: 'Enable 2FA',
      },
      backupCodes: {
        title: 'Backup Codes',
        description: 'Save these codes in a secure place. Each code can only be used once.',
        download: 'Download Codes',
        regenerate: 'Regenerate Codes',
        warning: 'These codes will not be shown again. Please save them now.',
      },
      methods: {
        title: '2FA Methods',
        totp: {
          name: 'Authenticator App',
          description: 'Use Google Authenticator, Authy, or similar apps',
        },
        email: {
          name: 'Email',
          description: 'Receive verification codes via email',
          enable: 'Enable Email 2FA',
          disable: 'Disable Email 2FA',
          codeSent: 'Verification code sent to your email',
        },
        sms: {
          name: 'SMS',
          description: 'Receive verification codes via SMS',
          enable: 'Enable SMS 2FA',
          disable: 'Disable SMS 2FA',
          codeSent: 'Verification code sent to your phone',
          phoneRequired: 'Please verify your phone number first',
        },
      },
      enterCode: 'Enter verification code',
      sendCode: 'Send Code',
      resendCode: 'Resend Code',
      codeExpires: 'Code expires in {time}',
    },
    phone: {
      title: 'Phone Number',
      description: 'Add and verify your phone number for SMS 2FA',
      verified: 'Verified',
      notVerified: 'Not verified',
      update: 'Update Phone',
      verify: 'Verify Phone',
      sendCode: 'Send Verification Code',
      enterCode: 'Enter the code sent to your phone',
      placeholder: 'Enter phone number (e.g., +86 138 1234 5678)',
    },
    sessions: {
      title: 'Active Sessions',
      description: 'Manage your active login sessions',
      current: 'Current Session',
      revokeAll: 'Revoke All Other Sessions',
      revoke: 'Revoke',
      device: 'Device',
      ip: 'IP Address',
      lastActive: 'Last Active',
    },
    apiKeys: {
      title: 'API Keys',
      description: 'Manage your API keys for programmatic access',
      create: 'Create New Key',
      noKeys: 'No API keys',
      name: 'Key Name',
      permissions: 'Permissions',
      created: 'Created',
      lastUsed: 'Last Used',
      expires: 'Expires',
      never: 'Never',
      revoke: 'Revoke',
      warning: 'This key will only be shown once. Please copy it now.',
    },
    changePassword: {
      title: 'Change Password',
      current: 'Current Password',
      new: 'New Password',
      confirm: 'Confirm New Password',
      submit: 'Change Password',
      success: 'Password changed successfully',
    },
    recovery: {
      title: 'Password Recovery',
      email: 'Recover via Email',
      sms: 'Recover via SMS',
      enterEmail: 'Enter your email address',
      enterPhone: 'Enter your phone number',
      sendResetLink: 'Send Reset Link',
      sendResetCode: 'Send Reset Code',
    },
  },

  // Wallet
  wallet: {
    title: 'Wallet',
    deposit: 'Deposit',
    withdraw: 'Withdraw',
    transfer: 'Transfer',
    history: 'History',
    asset: 'Asset',
    available: 'Available',
    inOrders: 'In Orders',
    totalBalance: 'Total Balance',
    noAssets: 'No assets found',
    depositAddress: 'Deposit Address',
    withdrawAddress: 'Withdrawal Address',
    amount: 'Amount',
    memo: 'Memo/Tag',
    network: 'Network',
    fee: 'Network Fee',
    minAmount: 'Minimum Amount',
    processing: 'Processing',
    confirmations: 'Confirmations',
  },

  // Trade
  trade: {
    title: 'Trade',
    buy: 'Buy',
    sell: 'Sell',
    market: 'Market',
    limit: 'Limit',
    stop: 'Stop',
    price: 'Price',
    amount: 'Amount',
    total: 'Total',
    available: 'Available',
    orderBook: 'Order Book',
    recentTrades: 'Recent Trades',
    openOrders: 'Open Orders',
    orderHistory: 'Order History',
    tradeHistory: 'Trade History',
    noOrders: 'No open orders',
    noTrades: 'No trades yet',
    cancel: 'Cancel',
    cancelAll: 'Cancel All',
    slippage: 'Slippage Tolerance',
  },

  // Profile
  profile: {
    title: 'Profile',
    username: 'Username',
    displayName: 'Display Name',
    email: 'Email',
    phone: 'Phone',
    bio: 'Bio',
    avatar: 'Avatar',
    changeAvatar: 'Change Avatar',
    joined: 'Member Since',
    kyc: {
      title: 'Identity Verification',
      level: 'Verification Level',
      unverified: 'Not Verified',
      basic: 'Basic Verified',
      advanced: 'Advanced Verified',
      startVerification: 'Start Verification',
      continueVerification: 'Continue Verification',
      limits: 'Trading Limits',
    },
  },

  // Errors
  errors: {
    general: 'Something went wrong. Please try again.',
    network: 'Network error. Please check your connection.',
    notFound: 'Page not found',
    unauthorized: 'Please login to continue',
    forbidden: 'You do not have permission to access this resource',
    serverError: 'Server error. Please try again later.',
    validation: 'Please check your input and try again',
    sessionExpired: 'Your session has expired. Please login again.',
  },

  // Time
  time: {
    justNow: 'Just now',
    minutesAgo: '{n} minutes ago',
    hoursAgo: '{n} hours ago',
    daysAgo: '{n} days ago',
    weeksAgo: '{n} weeks ago',
    monthsAgo: '{n} months ago',
    yearsAgo: '{n} years ago',
  },

  // Assets
  assets: {
    title: 'Digital Assets',
    subtitle: 'Explore tokenized digital assets',
    market: 'Asset Market',
    marketSubtitle: 'Discover and trade tokenized digital assets',
    myAssets: 'My Assets',
    myAssetsSubtitle: 'Manage your issued assets and applications',
    myApplications: 'My Applications',
    myHoldings: 'My Holdings',
    holdingsSubtitle: 'View your token balances',
    issuedAssets: 'Issued Assets',
    apply: 'Apply for Listing',
    noAssets: 'No assets available yet',
    noAssetsHint: 'Check back later for new listings',
    noApplications: 'No applications yet',
    noHoldings: 'No holdings yet',
    noIssuedAssets: 'You haven\'t issued any assets yet',
    noIssuedAssetsHint: 'Apply to list your first digital asset',

    // Asset details
    symbol: 'Symbol',
    name: 'Name',
    description: 'Description',
    assetType: 'Asset Type',
    totalSupply: 'Total Supply',
    circulatingSupply: 'Circulating Supply',
    decimals: 'Decimals',
    phase: 'Phase',
    issuer: 'Issuer',
    holders: 'Holders',
    listedAt: 'Listed At',
    createdAt: 'Created At',
    launchedAt: 'Launched At',
    price: 'Price',
    marketCap: 'Market Cap',
    bondingProgress: 'Bonding Progress',
    raised: 'Raised',
    complete: 'Complete',
    topHolders: 'Top Holders',

    // Trading
    trade: 'Trade',
    buy: 'Buy',
    sell: 'Sell',
    enterAmount: 'Enter amount',
    estimatedCost: 'Estimated Cost',
    estimatedReceive: 'Estimated Receive',
    advancedTrading: 'Advanced Trading',
    tradingSuspended: 'Trading is currently suspended for this asset',

    // Stats
    stats24h: '24h Statistics',
    volume24h: '24h Volume',
    high24h: '24h High',
    low24h: '24h Low',
    transactions: 'Transactions',

    // Asset types
    types: {
      database: 'Database',
      content: 'Content',
      infrastructure: 'Infrastructure',
      other: 'Other',
    },

    // Asset phases
    phases: {
      application: 'Application',
      bonding: 'Bonding Curve',
      trading: 'Trading',
      suspended: 'Suspended',
      delisted: 'Delisted',
    },

    // Application
    application: {
      title: 'Asset Listing Application',
      subtitle: 'Apply to list your digital asset on TokenForge',
      create: 'Create Application',
      edit: 'Edit Application',
      view: 'View Application',
      submit: 'Submit for Review',
      cancel: 'Cancel Application',
      saveDraft: 'Save as Draft',

      // Steps
      steps: {
        basicInfo: 'Basic Information',
        tokenomics: 'Tokenomics',
        bondingCurve: 'Bonding Curve',
        documents: 'Documents',
        review: 'Review & Submit',
      },

      // Fields
      fields: {
        name: 'Asset Name',
        nameHint: 'The official name of your asset (e.g., "DataToken")',
        symbol: 'Symbol',
        symbolHint: '2-10 uppercase letters/numbers (e.g., "DATA")',
        description: 'Description',
        descriptionHint: 'Describe your asset and its purpose',
        assetType: 'Asset Type',
        assetTypeHint: 'Select the category that best describes your asset',
        totalSupply: 'Total Supply',
        totalSupplyHint: 'Maximum number of tokens that will ever exist',
        decimals: 'Decimals',
        decimalsHint: 'Number of decimal places (0-18)',
        curveType: 'Curve Type',
        curveTypeHint: 'Select the bonding curve model',
        basePrice: 'Base Price (USDT)',
        basePriceHint: 'Starting price per token',
        slope: 'Slope',
        slopeHint: 'Price increase per token sold (for linear curve)',
        growthRate: 'Growth Rate',
        growthRateHint: 'Exponential growth rate (for exponential curve)',
        targetRaise: 'Target Raise (USDT)',
        targetRaiseHint: 'Total amount you aim to raise',
        launchThreshold: 'Launch Threshold (%)',
        launchThresholdHint: 'Percentage of target needed to launch trading',
        distributionPlan: 'Distribution Plan',
        distributionPlanHint: 'Describe how tokens will be distributed',
        ownershipProof: 'Ownership Proof',
        ownershipProofHint: 'Upload documents proving your ownership',
        revenueProof: 'Revenue Proof (Optional)',
        revenueProofHint: 'Upload historical revenue data if available',
        evaluationReport: 'Evaluation Report (Optional)',
        evaluationReportHint: 'Upload third-party valuation report',
      },

      // Curve types
      curveTypes: {
        linear: 'Linear',
        linearDesc: 'Price increases linearly with supply',
        exponential: 'Exponential',
        exponentialDesc: 'Price increases exponentially',
        sigmoid: 'Sigmoid (S-Curve)',
        sigmoidDesc: 'Gradual increase, then steep, then gradual',
      },

      // Status
      status: {
        draft: 'Draft',
        submitted: 'Submitted',
        reviewing: 'Under Review',
        approved: 'Approved',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
      },

      // Messages
      messages: {
        created: 'Application created successfully',
        updated: 'Application updated successfully',
        submitted: 'Application submitted for review',
        cancelled: 'Application cancelled',
        rejected: 'Your application was rejected',
        approved: 'Your application was approved!',
      },

      // Validation
      validation: {
        nameRequired: 'Asset name is required',
        symbolRequired: 'Symbol is required',
        symbolFormat: 'Symbol must be 2-10 uppercase letters/numbers',
        symbolTaken: 'This symbol is already taken',
        descriptionRequired: 'Description is required',
        totalSupplyRequired: 'Total supply is required',
        totalSupplyPositive: 'Total supply must be positive',
        basePriceRequired: 'Base price is required',
        targetRaiseRequired: 'Target raise amount is required',
        distributionPlanRequired: 'Distribution plan is required',
        ownershipProofRequired: 'At least one ownership proof document is required',
        slopeRequired: 'Slope is required for linear curve',
        growthRateRequired: 'Growth rate is required for exponential curve',
      },
    },

    // Statistics
    stats: {
      title: 'Asset Statistics',
      holderCount: 'Total Holders',
      largestHolding: 'Largest Holding',
      averageHolding: 'Average Holding',
      topHolders: 'Top Holders',
    },
  },

  // AMM Pool
  pool: {
    title: 'Liquidity Pools',
    subtitle: 'Trade tokens and provide liquidity',
    totalPools: 'Total Pools',
    totalTvl: 'Total TVL',
    volume24h: '24h Volume',
    activeTraders: 'Active Traders',
    noPoolsTitle: 'No Pools Available',
    noPoolsDescription: 'Liquidity pools will appear here once assets complete their bonding curve phase.',
    myPositions: 'My Positions',

    // Pool details
    detail: {
      title: 'Pool Details',
      reserves: 'Pool Reserves',
      tokenReserve: 'Token Reserve',
      quoteReserve: 'USDT Reserve',
      lpTotalSupply: 'Total LP Tokens',
      lpProviders: 'LP Providers',
      currentPrice: 'Current Price',
      tvl: 'Total Value Locked',
      fees24h: '24h Fees',
      apy: 'APY',
      feeRate: 'Fee Rate',
      recentTrades: 'Recent Trades',
      liquidityEvents: 'Liquidity Events',
    },

    // Chart
    chart: {
      loading: 'Loading chart...',
      noData: 'No chart data available',
      expand: 'Expand',
      collapse: 'Collapse',
      fullscreen: 'Fullscreen',
      exitFullscreen: 'Exit Fullscreen',
      intervals: {
        '1m': '1m',
        '5m': '5m',
        '15m': '15m',
        '1h': '1H',
        '4h': '4H',
        '1d': '1D',
        '1w': '1W',
      },
      types: {
        candlestick: 'Candlestick',
        line: 'Line',
        area: 'Area',
      },
      indicators: {
        title: 'Indicators',
        ma: 'MA',
        ema: 'EMA',
        rsi: 'RSI',
        macd: 'MACD',
        bb: 'Bollinger Bands',
      },
      stats: {
        price: 'Price',
        change24h: '24h Change',
        high24h: '24h High',
        low24h: '24h Low',
        volume24h: '24h Volume',
        trades24h: '24h Trades',
      },
    },

    // Swap
    swap: {
      title: 'Swap',
      from: 'From',
      to: 'To',
      balance: 'Balance',
      max: 'MAX',
      rate: 'Rate',
      priceImpact: 'Price Impact',
      fee: 'Fee',
      minReceived: 'Minimum Received',
      slippageTolerance: 'Slippage Tolerance',
      connectWallet: 'Connect Wallet',
      enterAmount: 'Enter Amount',
      insufficientBalance: 'Insufficient Balance',
      swap: 'Swap',
      swapping: 'Swapping...',
      swapSuccess: 'Swap completed successfully',
      swapFailed: 'Swap failed',
      settings: 'Swap Settings',
    },

    // Liquidity
    liquidity: {
      title: 'Manage Liquidity',
      add: 'Add Liquidity',
      remove: 'Remove Liquidity',
      addLiquidity: 'Add Liquidity',
      removeLiquidity: 'Remove Liquidity',
      poolShare: 'Pool Share',
      lpTokens: 'LP Tokens',
      lpTokensToReceive: 'LP Tokens to Receive',
      amountToRemove: 'Amount to Remove',
      outputEstimate: 'You will receive',
      slippageWarning: 'Output amounts are estimated. You will receive at least {percent}% of the shown amounts.',
      addSuccess: 'Liquidity added successfully',
      addFailed: 'Failed to add liquidity',
      removeSuccess: 'Liquidity removed successfully',
      removeFailed: 'Failed to remove liquidity',
      noLpTokens: 'No LP Tokens',
      insufficientToken: 'Insufficient {symbol} Balance',
      insufficientUsdt: 'Insufficient USDT Balance',
    },

    // Position
    position: {
      title: 'Your Position',
      noPosition: "You don't have any liquidity in this pool",
      lpBalance: 'LP Token Balance',
      tokenValue: 'Token Value',
      quoteValue: 'USDT Value',
      feesEarned: 'Fees Earned',
      managePosition: 'Manage Position',
    },

    // Admin
    admin: {
      title: 'Pool Management',
      subtitle: 'Manage AMM liquidity pools',
      createPool: 'Create Pool',
      pausePool: 'Pause Pool',
      resumePool: 'Resume Pool',
      updateFee: 'Update Fee Rate',
      paused: 'Pool paused successfully',
      resumed: 'Pool resumed successfully',
      feeUpdated: 'Fee rate updated successfully',
      poolCreated: 'Pool created successfully',
      confirmPause: 'Are you sure you want to pause this pool? Users will not be able to swap or add liquidity.',
      topLpHolders: 'Top LP Holders',
      recentSwaps: 'Recent Swaps',
      statsOverview: 'Statistics Overview',
    },
  },
};
